import { useEffect, useState, useSyncExternalStore } from 'react';
import { resolveProfilePictureSource } from '@wlb/shared/utils/imageUrl';
import { UserIcon } from './icons';
import { avatarFallback } from './avatarFallback';
import {
  getRegisteredPersonAvatar,
  registerPersonAvatar,
  subscribeToPersonAvatars,
} from './personAvatarRegistry';
import './avatar.css';

export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  src?: unknown;
  /** Fallback label for non-person entities without a username. */
  name?: string;
  /** Preferred initial and canonical tone source for people. */
  username?: string | null;
  /**
   * Stable identity (profileUserGuid, userId, ...) that fixes the fallback
   * color. Falls back to the name when a surface has no id to hand.
   */
  seed?: string | number | null;
  profileUserGuid?: string | null;
  userId?: string | number | null;
  /**
   * Marks `src` as the current value from the profile endpoint. Canonical
   * values replace stale `userPhoto` snapshots on every other surface.
   */
  canonical?: boolean;
  size?: AvatarSize;
  /** Rounded-square instead of a circle (profile header in the Figma design). */
  square?: boolean;
  className?: string;
}

const ICON_SIZE: Record<AvatarSize, number> = { sm: 16, md: 20, lg: 24, xl: 36 };

export function Avatar({
  src,
  name,
  username,
  seed,
  profileUserGuid,
  userId,
  canonical = false,
  size = 'md',
  square = false,
  className = '',
}: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const resolvedSource = resolveProfilePictureSource(src);
  const directSrc =
    resolvedSource && typeof resolvedSource !== 'number'
      ? (Array.isArray(resolvedSource) ? resolvedSource[0] : resolvedSource)?.uri ?? null
      : null;
  const registeredSrc = useSyncExternalStore(
    subscribeToPersonAvatars,
    () => getRegisteredPersonAvatar({ profileUserGuid, userId, username }),
    () => undefined,
  );
  const resolvedSrc = registeredSrc !== undefined ? registeredSrc : directSrc;

  useEffect(() => {
    if (!canonical) return;
    registerPersonAvatar(
      { profileUserGuid, userId, username },
      directSrc,
    );
  }, [canonical, directSrc, profileUserGuid, userId, username]);
  const classes = [
    'ui-avatar',
    `ui-avatar--${size}`,
    square ? 'ui-avatar--square' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const { initial, tone } = avatarFallback({ seed, name, username });
  const showImage = Boolean(resolvedSrc && failedSrc !== resolvedSrc);

  return (
    <span
      className={classes}
      data-avatar-tone={showImage || !initial ? undefined : tone}
      aria-hidden
    >
      {/* The surrounding control already names the person, so the image is decorative. */}
      {showImage ? (
        <img
          src={resolvedSrc ?? undefined}
          alt=""
          aria-hidden
          onError={() => setFailedSrc(resolvedSrc)}
        />
      ) : initial || <UserIcon size={ICON_SIZE[size]} />}
    </span>
  );
}
